import { BetItemFragment, BetSlipFragment, BetTypeEnum, MultiGameParlayStatusEnum } from "../../graphql/core-types";
import { formatBetLineLabel, formatMultiplier, sortBetItems } from "../../helpers/bet-line-helpers";
import { getBetItemTitle, getBountyTitle } from "../../helpers/get-bet-item-title";
import { useInternalization } from "../../context/cms-context";

export type LegStatus = 'pending' | 'won' | 'lost' | 'refunded' | 'cancelled'

export type LegRow = {
  key: string
  label: string
  status: LegStatus
  multiplier?: string | null
}

// mirrors how plc-app splits a slip: plain lines, single game parlays grouped by parlay id, and multi game parlays
export type LegGroup =
  | { kind: 'line', key: string, row: LegRow }
  | { kind: 'parlay', key: string, title: string, status: LegStatus, progress: string | null, multiplier: string | null, legs: LegRow[] }
  | { kind: 'multiGame', key: string, title: string, status: LegStatus, progress: string, multiplier: string | null, requirements: LegRow[], matches: LegStatus[] }

const betItemStatus = (betItem: BetItemFragment): LegStatus => {
  if (betItem.resolved && betItem.refunded) return 'refunded'
  if (!betItem.resolved) return 'pending'
  return betItem.won ? 'won' : 'lost'
}

const multiGameLegStatus = (status: MultiGameParlayStatusEnum): LegStatus => {
  switch (status) {
    case MultiGameParlayStatusEnum.WON: return 'won'
    case MultiGameParlayStatusEnum.LOST: return 'lost'
    case MultiGameParlayStatusEnum.CANCELLED: return 'cancelled'
    default: return 'pending'
  }
}

export const summariseLegGroup = (group: LegGroup): string => {
  switch (group.kind) {
    case 'line': return group.row.label
    case 'parlay': return `${group.title}: ${group.legs.map((leg) => leg.label).join(', ')}`
    case 'multiGame': return `${group.title}: ${group.requirements.map((requirement) => requirement.label).join(', ')}`
  }
}

export const useBetLegs = (betSlip: BetSlipFragment) => {
  const { games, translate } = useInternalization()

  const betItems = betSlip.betItems || []

  const gameName = betItems?.[0]?.gameSlug
  const betType = betItems?.[0]?.betType
  const gameModeRaw =
    betItems?.[0]?.gameData?.gameMode || betItems?.[0]?.gameData?.map

  const game = games.find(g => g.systemId === gameName)

  const gameMode = game?.gamemodes?.find(gm => gm.systemId === gameModeRaw)

  const betLineOrder = gameMode?.betLineDisplayOrder
  const orderedBetItems = sortBetItems(betItems, betLineOrder).filter((betItem): betItem is BetItemFragment => Boolean(betItem))

  // scoreNoun is in the cms config but not on the generated Game type
  const gamesWithScoreNoun = games as { systemId?: string | null, scoreNoun?: string | null }[]

  const lineTitle = (betItem: BetItemFragment): string => {
    if (betItem.betType === BetTypeEnum.SINGLE_GAME_BOUNTY) {
      return getBountyTitle({
        translate,
        title: betItem.title || betItem.type,
        bountyIncrement: betItem.metadata?.singleGameBounty?.ranges?.[0]?.bountyIncrement || '1',
      })
    }
    return getBetItemTitle({
      title: betItem.title || betItem.type,
      value: betItem.linesValue,
      translate,
      game: betItem.gameSlug,
      games: gamesWithScoreNoun,
    })
  }

  const legGroups: LegGroup[] = []
  const parlayGroupIndexById = new Map<string, number>()
  const parlayLegsById = new Map<string, BetItemFragment[]>()

  orderedBetItems.forEach((betItem) => {
    const singleGameParlay = betItem.metadata?.singleGameParlay
    const multiGameParlay = betItem.metadata?.multiGameParlay

    if (singleGameParlay) {
      // every leg of a single game parlay is its own bet item sharing the parlay id
      const legs = parlayLegsById.get(singleGameParlay.id) || []
      legs.push(betItem)
      parlayLegsById.set(singleGameParlay.id, legs)
      if (!parlayGroupIndexById.has(singleGameParlay.id)) {
        parlayGroupIndexById.set(singleGameParlay.id, legGroups.length)
        legGroups.push({ kind: 'parlay', key: singleGameParlay.id, title: '', status: 'pending', progress: null, multiplier: null, legs: [] })
      }
      return
    }

    if (multiGameParlay) {
      // requirements are what every match has to hit, legs are the matches themselves
      const matches = multiGameParlay.legs.map((leg) => multiGameLegStatus(leg.status))
      const successes = matches.filter((match) => match === 'won').length
      const failures = matches.filter((match) => match === 'lost').length
      const matchesRequired = multiGameParlay.successesRequired + multiGameParlay.failuresAllowed

      let status: LegStatus = 'pending'
      if (betItem.resolved && betItem.refunded) status = 'refunded'
      else if (failures > multiGameParlay.failuresAllowed) status = 'lost'
      else if (successes >= multiGameParlay.successesRequired) status = 'won'

      legGroups.push({
        kind: 'multiGame',
        key: betItem.id,
        title: multiGameParlay.failuresAllowed >= 1
          ? translate('bets-mmb_conditions_with_failures', { matches_required: matchesRequired, successes_needed: multiGameParlay.successesRequired })
          : translate('bets-mmb_conditions_with_no_failures', { matches_required: matchesRequired }),
        status,
        progress: `${successes}/${multiGameParlay.successesRequired}`,
        multiplier: formatMultiplier(betItem.payoutMultiplier),
        requirements: multiGameParlay.requirements.filter(Boolean).map((requirement, index) => ({
          key: `${betItem.id}-${index}`,
          label: getBetItemTitle({
            title: requirement!.lineType,
            value: requirement!.title,
            translate,
            game: betItem.gameSlug,
            games: gamesWithScoreNoun,
          }),
          status,
        })),
        matches,
      })
      return
    }

    legGroups.push({ kind: 'line', key: betItem.id, row: { key: betItem.id, label: lineTitle(betItem), status: betItemStatus(betItem), multiplier: formatMultiplier(betItem.payoutMultiplier) } })
  })

  parlayLegsById.forEach((legs, parlayId) => {
    const legRows = legs.map((leg) => ({ key: leg.id, label: lineTitle(leg), status: betItemStatus(leg) }))
    const wonCount = legRows.filter((leg) => leg.status === 'won').length
    const isRefunded = legRows.every((leg) => leg.status === 'refunded')
    const isResolved = legRows.every((leg) => leg.status !== 'pending')
    const numberOfLegs = legs[0].metadata?.singleGameParlay?.numberOfLegs || legs.length

    let status: LegStatus = 'pending'
    if (isRefunded) status = 'refunded'
    else if (legRows.some((leg) => leg.status === 'lost')) status = 'lost'
    else if (isResolved) status = 'won'

    legGroups[parlayGroupIndexById.get(parlayId)!] = {
      kind: 'parlay',
      key: parlayId,
      title: translate('parlay-number_of_legs', { number_of_legs: numberOfLegs }),
      status,
      progress: isResolved && !isRefunded
        ? translate('activity-x_of_x', { first_count: wonCount, secound_count: legRows.length })
        : null,
      // every leg carries the parlay's combined multiplier, there's no per leg value
      multiplier: formatMultiplier(legs[0].payoutMultiplier),
      legs: legRows,
    }
  })

  const isParlay = legGroups.some((group) => group.kind !== 'line')
  const betTypeLabel = isParlay ? null : betType && formatBetLineLabel(betType)

  return { game, gameName, gameMode, gameModeSystemId: gameMode?.systemId || gameModeRaw, betTypeLabel, legGroups }
}
